import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { usePIM } from '../../context/PIMContext';
import { 
    Check, AlertTriangle, Shield, Layers, FileSpreadsheet, 
    RefreshCw, X, ArrowRight, Lock, CheckCircle2, AlertCircle 
} from 'lucide-react';

export const IMEIManagementModal = ({ 
    isOpen, 
    onClose, 
    modelName, 
    sku, 
    orderNumber, 
    chargeId, 
    batchNumber, 
    deviceQuantity,
    onAllocationConfirmed 
}) => {
    const { user } = usePIM();
    const isAdmin = user?.role === 'Admin' || user?.username === 'ulrix' || user?.username === 'admin';
    const isEditor = isAdmin || user?.role === 'Editor';

    const [activeTab, setActiveTab] = useState('charge_alloc'); // charge_alloc, allocations, pools, import_vault
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Context & Allocation State
    const [contextData, setContextData] = useState(null);
    const [pools, setPools] = useState([]);
    const [selectedPoolId, setSelectedPoolId] = useState('');
    const [calcData, setCalcData] = useState(null);
    const [imeisPerDevice, setImeisPerDevice] = useState(1);

    // Filter for existing allocations
    const [allocFilter, setAllocFilter] = useState({ model: '', sku: '', status: '', order: '' });
    const [allocationsList, setAllocationsList] = useState([]);

    // Pool creation modal / form
    const [showNewPoolForm, setShowNewPoolForm] = useState(false);
    const [newPool, setNewPool] = useState({
        name: '',
        startBasisStr: '',
        endExclusiveStr: '',
        tac: '',
        allowedModels: modelName || '',
        sourceRef: ''
    });

    // Excel & Vault Reconcile
    const [excelData, setExcelData] = useState(null);
    const [selectedSheet, setSelectedSheet] = useState('');
    const [vaultMatrix, setVaultMatrix] = useState(null);

    // Revoke reason dialog
    const [revokingAllocId, setRevokingAllocId] = useState(null);
    const [revokeReason, setRevokeReason] = useState('');

    const token = localStorage.getItem('token');

    // 1. Fetch charge context & pools on open
    const loadContext = async () => {
        if (!isOpen) return;
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (chargeId) params.append('charge_id', chargeId);
            if (modelName) params.append('model_name', modelName);
            if (sku) params.append('sku', sku);
            if (orderNumber) params.append('order_number', orderNumber);

            const res = await fetch(`/api/imei/charge-context?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Fehler beim Laden des Charge-Kontexts.");
            const data = await res.json();
            setContextData(data);
            if (data.eligiblePools && data.eligiblePools.length > 0) {
                setSelectedPoolId(data.eligiblePools[0].id);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadPools = async () => {
        try {
            const res = await fetch('/api/imei/pools', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPools(data);
            }
        } catch (err) {
            console.error("Fehler beim Laden der Pools:", err);
        }
    };

    const loadAllocations = async () => {
        try {
            const params = new URLSearchParams();
            if (allocFilter.model) params.append('model', allocFilter.model);
            if (allocFilter.sku) params.append('sku', allocFilter.sku);
            if (allocFilter.status) params.append('status', allocFilter.status);
            if (allocFilter.order) params.append('order', allocFilter.order);

            const res = await fetch(`/api/imei/allocations?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAllocationsList(data);
            }
        } catch (err) {
            console.error("Fehler beim Laden der Zuteilungen:", err);
        }
    };

    const calculateNeed = async () => {
        const qty = parseInt(deviceQuantity, 10);
        if (isNaN(qty) || qty <= 0) {
            setCalcData(null);
            return;
        }
        try {
            const res = await fetch('/api/imei/calculate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ deviceQuantity: qty, imeisPerDevice })
            });
            if (res.ok) {
                const data = await res.json();
                setCalcData(data);
            }
        } catch (err) {
            console.error("Fehler bei Berechnung:", err);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadContext();
            loadPools();
            calculateNeed();
        }
    }, [isOpen, chargeId, modelName, sku, deviceQuantity, imeisPerDevice]);

    useEffect(() => {
        if (activeTab === 'allocations') {
            loadAllocations();
        }
    }, [activeTab, allocFilter]);

    // Handle Provisional Allocate
    const handleProvisionalAllocate = async () => {
        if (!chargeId) {
            setError("Eine persistente vorläufige Zuteilung erfordert eine bereits gespeicherte Charge. Bitte speichern Sie die Charge zuerst.");
            return;
        }
        if (!selectedPoolId) {
            setError("Bitte wählen Sie einen freigegebenen Nummernpool.");
            return;
        }
        if (!deviceQuantity || parseInt(deviceQuantity, 10) <= 0) {
            setError("Gerätemenge fehlt oder ist ungültig.");
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMsg('');
        try {
            const res = await fetch('/api/imei/provisional-allocate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    pool_id: selectedPoolId,
                    model_name: modelName,
                    sku,
                    order_number: orderNumber,
                    charge_id: chargeId,
                    device_quantity: deviceQuantity,
                    imeis_per_device: imeisPerDevice
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Zuteilung fehlgeschlagen.");
            setSuccessMsg(`Bereich erfolgreich vorläufig zugeteilt: ${data.startStr} - ${data.lastContainedStr} (${data.quantity} Basiswerte).`);
            loadContext();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle Confirm Allocation (Admin only)
    const handleConfirm = async (allocId, revision) => {
        if (!isAdmin) {
            setError("Nur autorisierte Administratoren dürfen Zuteilungen verbindlich bestätigen.");
            return;
        }
        setLoading(true);
        setError('');
        setSuccessMsg('');
        try {
            const res = await fetch(`/api/imei/confirm-allocation/${allocId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ revision })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Bestätigung fehlgeschlagen.");
            setSuccessMsg("Zuteilung verbindlich bestätigt und in Charge-Felder übernommen.");
            if (onAllocationConfirmed) {
                onAllocationConfirmed(data.imeiStart, data.imeiEnd);
            }
            loadContext();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle Revoke
    const handleRevoke = async () => {
        if (!revokingAllocId || !revokeReason.trim()) {
            setError("Bitte geben Sie eine Begründung für das Aufheben an.");
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`/api/imei/revoke-allocation/${revokingAllocId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ reason: revokeReason.trim() })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Aufheben fehlgeschlagen.");
            setSuccessMsg("Zuteilung erfolgreich aufgehoben.");
            setRevokingAllocId(null);
            setRevokeReason('');
            loadContext();
            if (activeTab === 'allocations') loadAllocations();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle Pool Creation
    const handleCreatePool = async (e) => {
        e.preventDefault();
        if (!isAdmin) return;
        setLoading(true);
        setError('');
        try {
            const models = newPool.allowedModels.split(',').map(m => m.trim()).filter(Boolean);
            const res = await fetch('/api/imei/pools', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    name: newPool.name,
                    startBasisStr: newPool.startBasisStr,
                    endExclusiveStr: newPool.endExclusiveStr,
                    tac: newPool.tac,
                    allowedModels: models,
                    sourceRef: newPool.sourceRef
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Fehler beim Erstellen des Pools.");
            setSuccessMsg(`Pool '${data.name}' erfolgreich angelegt.`);
            setShowNewPoolForm(false);
            setNewPool({ name: '', startBasisStr: '', endExclusiveStr: '', tac: '', allowedModels: '', sourceRef: '' });
            loadPools();
            loadContext();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load Excel Preview
    const loadExcelPreview = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch('/api/imei/excel-preview', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Fehler beim Laden der Excel-Vorschau.");
            const data = await res.json();
            setExcelData(data);
            const sheetNames = Object.keys(data.sheets || {});
            if (sheetNames.length > 0 && !selectedSheet) {
                setSelectedSheet(sheetNames[0]);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Load Vault Reconcile
    const loadVaultReconcile = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch('/api/imei/vault-reconcile', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error("Fehler beim Laden des Vault-Abgleichs.");
            const data = await res.json();
            setVaultMatrix(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title="IMEI-Management & Bereichszuteilung"
            maxWidth="max-w-5xl"
        >
            <div className="flex flex-col h-[75vh] -mx-4 -my-4 p-4 text-stone-200">
                {/* Tabs */}
                <div className="flex border-b border-white/10 gap-2 mb-4">
                    <button
                        onClick={() => setActiveTab('charge_alloc')}
                        className={`px-4 py-2 font-medium text-xs rounded-t-lg transition flex items-center gap-2 ${
                            activeTab === 'charge_alloc'
                                ? 'bg-stone-800 text-blue-400 border-t-2 border-blue-500'
                                : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800/40'
                        }`}
                    >
                        <Layers size={14} /> Zuteilung für diese Charge
                    </button>
                    <button
                        onClick={() => setActiveTab('allocations')}
                        className={`px-4 py-2 font-medium text-xs rounded-t-lg transition flex items-center gap-2 ${
                            activeTab === 'allocations'
                                ? 'bg-stone-800 text-blue-400 border-t-2 border-blue-500'
                                : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800/40'
                        }`}
                    >
                        <Shield size={14} /> Bestehende Zuteilungen
                    </button>
                    <button
                        onClick={() => setActiveTab('pools')}
                        className={`px-4 py-2 font-medium text-xs rounded-t-lg transition flex items-center gap-2 ${
                            activeTab === 'pools'
                                ? 'bg-stone-800 text-blue-400 border-t-2 border-blue-500'
                                : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800/40'
                        }`}
                    >
                        <Lock size={14} /> Nummernpools
                    </button>
                    <button
                        onClick={() => setActiveTab('import_vault')}
                        className={`px-4 py-2 font-medium text-xs rounded-t-lg transition flex items-center gap-2 ${
                            activeTab === 'import_vault'
                                ? 'bg-stone-800 text-blue-400 border-t-2 border-blue-500'
                                : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800/40'
                        }`}
                    >
                        <FileSpreadsheet size={14} /> Import & Vault-Abgleich
                    </button>
                </div>

                {/* Status- / Fehlermeldungen */}
                {error && (
                    <div className="bg-red-900/30 border border-red-800 text-red-300 p-3 rounded-lg text-xs flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} className="text-red-400 flex-shrink-0" />
                        <span>{error}</span>
                    </div>
                )}
                {successMsg && (
                    <div className="bg-emerald-900/30 border border-emerald-800 text-emerald-300 p-3 rounded-lg text-xs flex items-center gap-2 mb-3">
                        <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0" />
                        <span>{successMsg}</span>
                    </div>
                )}

                {/* Tab-Inhalte */}
                <div className="flex-1 overflow-y-auto pr-1">
                    {/* 1. TAB: ZUTEILUNG FÜR DIESE CHARGE */}
                    {activeTab === 'charge_alloc' && (
                        <div className="space-y-4">
                            {/* Kontext-Karten */}
                            <div className="grid grid-cols-4 gap-3 bg-stone-900/60 border border-white/5 p-3 rounded-lg text-xs">
                                <div>
                                    <span className="text-stone-500 block uppercase">Modell</span>
                                    <span className="font-semibold text-white">{modelName || '-'}</span>
                                </div>
                                <div>
                                    <span className="text-stone-500 block uppercase">SKU</span>
                                    <span className="font-semibold text-white">{sku || '-'}</span>
                                </div>
                                <div>
                                    <span className="text-stone-500 block uppercase">Charge (Batch)</span>
                                    <span className="font-semibold text-white">{batchNumber || (chargeId ? `#${chargeId}` : '(noch ungespeichert)')}</span>
                                </div>
                                <div>
                                    <span className="text-stone-500 block uppercase">Auftrag (Order)</span>
                                    <span className="font-semibold text-white">{orderNumber || '-'}</span>
                                </div>
                            </div>

                            {/* Ungespeicherte Charge Hinweis */}
                            {!chargeId && (
                                <div className="bg-amber-900/20 border border-amber-800/40 p-3 rounded-lg text-xs text-amber-300 flex items-start gap-2">
                                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold">Charge noch nicht persistent gespeichert</div>
                                        <div>Einsicht und Berechnung sind erlaubt. Eine verbindliche Zuteilung erfordert das vorherige Speichern der Charge im Chargenfenster.</div>
                                    </div>
                                </div>
                            )}

                            {/* Bedarfs- und Rechenbox */}
                            <div className="bg-stone-900/40 border border-white/5 p-4 rounded-lg space-y-3">
                                <div className="text-xs font-semibold uppercase text-stone-400 flex justify-between items-center">
                                    <span>Bedarf & 4 % feste Reserve</span>
                                    <span className="text-stone-500 font-mono text-[11px]">14-stellige Basis · Fabrik generiert Prüfziffer</span>
                                </div>

                                <div className="grid grid-cols-4 gap-3 text-xs items-center">
                                    <div>
                                        <label className="text-stone-500 block mb-1">Gerätemenge (Charge)</label>
                                        <div className="font-mono text-white text-sm bg-black/30 px-2 py-1.5 rounded border border-white/5">
                                            {deviceQuantity || '0'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-stone-500 block mb-1">IMEIs je Gerät</label>
                                        <select 
                                            value={imeisPerDevice} 
                                            onChange={(e) => setImeisPerDevice(parseInt(e.target.value, 10))}
                                            className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white font-mono"
                                        >
                                            <option value={1}>1 (Single-SIM)</option>
                                            <option value={2}>2 (Dual-SIM)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-stone-500 block mb-1">Feste Reserve (4 % aufgerundet)</label>
                                        <div className="font-mono text-amber-400 text-sm bg-black/30 px-2 py-1.5 rounded border border-white/5">
                                            +{calcData ? calcData.reserveQuantity : 0} Basiswerte
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-stone-500 block mb-1">Zuteilungsmenge gesamt</label>
                                        <div className="font-mono text-emerald-400 font-bold text-sm bg-black/30 px-2 py-1.5 rounded border border-white/5">
                                            {calcData ? calcData.totalQuantity : 0} Basiswerte
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Aktive Zuteilung zur Charge vorhanden? */}
                            {contextData?.activeAllocation ? (
                                <div className="border border-blue-500/30 bg-blue-950/20 p-4 rounded-lg space-y-3">
                                    <div className="flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-semibold uppercase text-blue-400">Zugewiesener IMEI-Bereich</span>
                                            <Badge color={contextData.activeAllocation.status === 'confirmed' ? '#10b981' : '#f59e0b'}>
                                                {contextData.activeAllocation.status === 'confirmed' ? 'Bestätigt' : 'Vorläufig zugeteilt'}
                                            </Badge>
                                        </div>
                                        <span className="text-xs text-stone-500">
                                            Pool: <strong className="text-stone-300">{contextData.activeAllocation.pool_name || '-'}</strong>
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3 bg-black/40 p-3 rounded font-mono text-xs">
                                        <div>
                                            <span className="text-stone-500 block text-[10px] uppercase">Erste Basis</span>
                                            <span className="text-white font-bold">{contextData.activeAllocation.start_str}</span>
                                        </div>
                                        <div>
                                            <span className="text-stone-500 block text-[10px] uppercase">Letzte enthaltene Basis</span>
                                            <span className="text-white font-bold">{contextData.activeAllocation.last_contained_str}</span>
                                        </div>
                                        <div>
                                            <span className="text-stone-500 block text-[10px] uppercase">Basiswerte gesamt</span>
                                            <span className="text-emerald-400 font-bold">{contextData.activeAllocation.quantity}</span>
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center pt-2">
                                        <span className="text-[11px] text-stone-400">
                                            {contextData.activeAllocation.status === 'provisional' 
                                                ? "Wartet auf Bestätigung durch Administrator."
                                                : `Bestätigt von ${contextData.activeAllocation.confirmed_by || 'Admin'}.`}
                                        </span>
                                        <div className="flex gap-2">
                                            {contextData.activeAllocation.status === 'provisional' && isAdmin && (
                                                <PrimaryButton 
                                                    onClick={() => handleConfirm(contextData.activeAllocation.id, contextData.activeAllocation.revision)}
                                                    disabled={loading}
                                                >
                                                    <Check size={14} className="mr-1" /> Zuteilung bestätigen
                                                </PrimaryButton>
                                            )}
                                            {isAdmin && (
                                                <GhostButton 
                                                    onClick={() => setRevokingAllocId(contextData.activeAllocation.id)}
                                                    disabled={loading}
                                                    className="text-red-400 border-red-900/40 hover:bg-red-900/20"
                                                >
                                                    <X size={14} className="mr-1" /> Aufheben...
                                                </GhostButton>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Keine Zuteilung vorhanden -> Zuteilungs-Workflow */
                                <div className="bg-stone-900/40 border border-white/5 p-4 rounded-lg space-y-4">
                                    <div className="text-xs font-semibold uppercase text-stone-300">
                                        Neuen Bereich vorläufig zuteilen
                                    </div>

                                    {contextData?.eligiblePools && contextData.eligiblePools.length > 0 ? (
                                        <div className="space-y-3">
                                            <div>
                                                <label className="text-xs text-stone-400 block mb-1">
                                                    Freigegebener Pool für {modelName || 'Modell'}:
                                                </label>
                                                <select
                                                    value={selectedPoolId}
                                                    onChange={(e) => setSelectedPoolId(e.target.value)}
                                                    className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-xs"
                                                >
                                                    {contextData.eligiblePools.map(p => (
                                                        <option key={p.id} value={p.id}>
                                                            {p.name} ({p.start_str} - {p.last_contained_str}) · Freie Kapazität: {p.remaining_capacity}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex justify-end pt-2">
                                                <PrimaryButton 
                                                    onClick={handleProvisionalAllocate}
                                                    disabled={loading || !chargeId || !calcData?.totalQuantity}
                                                >
                                                    <Layers size={14} className="mr-1" /> Bereich vorläufig zuteilen
                                                </PrimaryButton>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-xs text-stone-400 p-3 bg-black/20 rounded">
                                            Kein aktiver Nummernpool für Modell <strong>{modelName || '-'}</strong> freigegeben. Bitte legen Sie unter 'Nummernpools' einen Pool an oder weisen Sie das Modell zu.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 2. TAB: BESTEHENDE ZUTEILUNGEN */}
                    {activeTab === 'allocations' && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-4 gap-2 text-xs">
                                <Input 
                                    placeholder="Modell filtern..." 
                                    value={allocFilter.model} 
                                    onChange={(e) => setAllocFilter({ ...allocFilter, model: e.target.value })} 
                                />
                                <Input 
                                    placeholder="SKU filtern..." 
                                    value={allocFilter.sku} 
                                    onChange={(e) => setAllocFilter({ ...allocFilter, sku: e.target.value })} 
                                />
                                <Input 
                                    placeholder="Auftrag filtern..." 
                                    value={allocFilter.order} 
                                    onChange={(e) => setAllocFilter({ ...allocFilter, order: e.target.value })} 
                                />
                                <select 
                                    value={allocFilter.status}
                                    onChange={(e) => setAllocFilter({ ...allocFilter, status: e.target.value })}
                                    className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs"
                                >
                                    <option value="">Alle Status</option>
                                    <option value="provisional">Vorläufig</option>
                                    <option value="confirmed">Bestätigt</option>
                                    <option value="released_to_factory">An Fabrik freigegeben</option>
                                    <option value="usage_proven">Verwendung nachgewiesen</option>
                                    <option value="revoked">Aufgehoben</option>
                                </select>
                            </div>

                            <div className="bg-black/20 border border-white/5 rounded-lg overflow-x-auto max-h-[50vh]">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead className="sticky top-0 bg-stone-900 border-b border-white/10 text-stone-400">
                                        <tr>
                                            <th className="p-2">ID</th>
                                            <th className="p-2">Modell / SKU</th>
                                            <th className="p-2">Auftrag / Charge</th>
                                            <th className="p-2">Erste Basis</th>
                                            <th className="p-2">Letzte Basis</th>
                                            <th className="p-2 text-right">Menge</th>
                                            <th className="p-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allocationsList.map((a) => (
                                            <tr key={a.id} className="border-b border-white/5 hover:bg-stone-800/30 font-mono">
                                                <td className="p-2 text-stone-400">#{a.id}</td>
                                                <td className="p-2 font-sans font-medium text-white">{a.model_name} <span className="text-stone-500 block text-[11px]">{a.sku || '-'}</span></td>
                                                <td className="p-2 font-sans text-stone-300">{a.order_number || '-'} <span className="text-stone-500 block text-[11px]">{a.charge_batch || '-'}</span></td>
                                                <td className="p-2 text-stone-300">{a.start_str}</td>
                                                <td className="p-2 text-stone-300">{a.last_contained_str}</td>
                                                <td className="p-2 text-right text-emerald-400 font-bold">{a.quantity}</td>
                                                <td className="p-2 font-sans">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] border ${
                                                        a.status === 'confirmed' ? 'bg-emerald-950/40 border-emerald-500 text-emerald-300' :
                                                        a.status === 'provisional' ? 'bg-amber-950/40 border-amber-500 text-amber-300' :
                                                        a.status === 'revoked' ? 'bg-stone-800 border-stone-600 text-stone-500 line-through' :
                                                        'bg-blue-950/40 border-blue-500 text-blue-300'
                                                    }`}>
                                                        {a.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* 3. TAB: NUMMERNPOOLS */}
                    {activeTab === 'pools' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-stone-400">Verwaltete freigegebene IMEI-Nummernblöcke</span>
                                {isAdmin && !showNewPoolForm && (
                                    <PrimaryButton onClick={() => setShowNewPoolForm(true)}>
                                        + Neuen Pool erfassen
                                    </PrimaryButton>
                                )}
                            </div>

                            {/* Neuer Pool Dialog */}
                            {showNewPoolForm && (
                                <form onSubmit={handleCreatePool} className="bg-stone-900 border border-blue-500/30 p-4 rounded-lg space-y-3 text-xs">
                                    <div className="font-semibold text-white">Neuen IMEI-Nummernpool erfassen</div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-stone-400 block mb-1">Bezeichnung</label>
                                            <Input 
                                                value={newPool.name} 
                                                onChange={(e) => setNewPool({ ...newPool, name: e.target.value })} 
                                                placeholder="z.B. 4G Keyphones 2026" 
                                                required 
                                            />
                                        </div>
                                        <div>
                                            <label className="text-stone-400 block mb-1">TAC (falls bestätigt)</label>
                                            <Input 
                                                value={newPool.tac} 
                                                onChange={(e) => setNewPool({ ...newPool, tac: e.target.value })} 
                                                placeholder="z.B. 35266376" 
                                            />
                                        </div>
                                        <div>
                                            <label className="text-stone-400 block mb-1">Anfang (14 Stellen)</label>
                                            <Input 
                                                value={newPool.startBasisStr} 
                                                onChange={(e) => setNewPool({ ...newPool, startBasisStr: e.target.value })} 
                                                placeholder="35266376000000" 
                                                maxLength={14} 
                                                required 
                                            />
                                        </div>
                                        <div>
                                            <label className="text-stone-400 block mb-1">Exklusives Ende (14 Stellen)</label>
                                            <Input 
                                                value={newPool.endExclusiveStr} 
                                                onChange={(e) => setNewPool({ ...newPool, endExclusiveStr: e.target.value })} 
                                                placeholder="35266376200000" 
                                                maxLength={14} 
                                                required 
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-stone-400 block mb-1">Freigegebene Modelle (Kommagetrennt)</label>
                                            <Input 
                                                value={newPool.allowedModels} 
                                                onChange={(e) => setNewPool({ ...newPool, allowedModels: e.target.value })} 
                                                placeholder="SL605LTE, SL645LTE" 
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-2 pt-2">
                                        <GhostButton type="button" onClick={() => setShowNewPoolForm(false)}>Abbrechen</GhostButton>
                                        <PrimaryButton type="submit" disabled={loading}>Speichern</PrimaryButton>
                                    </div>
                                </form>
                            )}

                            {/* Pool-Liste */}
                            <div className="grid grid-cols-1 gap-3">
                                {pools.map((p) => (
                                    <div key={p.id} className="bg-stone-900/50 border border-white/5 p-3 rounded-lg text-xs space-y-2">
                                        <div className="flex justify-between items-center">
                                            <div className="font-bold text-white text-sm flex items-center gap-2">
                                                {p.name}
                                                <Badge color={p.status === 'active' ? '#10b981' : '#ef4444'}>{p.status}</Badge>
                                            </div>
                                            <span className="font-mono text-stone-400">TAC: {p.tac || '-'}</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 bg-black/30 p-2 rounded font-mono text-[11px]">
                                            <div>Erste Basis: <span className="text-stone-200">{p.start_str}</span></div>
                                            <div>Letzte Basis: <span className="text-stone-200">{p.last_contained_str}</span></div>
                                            <div className="text-right">Frei: <strong className="text-emerald-400">{p.remaining_capacity}</strong> / {p.total_capacity}</div>
                                        </div>
                                        <div className="text-[11px] text-stone-400">
                                            Freigegebene Modelle: <span className="text-stone-300 font-medium">{(p.allowed_models || []).join(', ') || 'Alle'}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 4. TAB: IMPORT & VAULT-ABGLEICH */}
                    {activeTab === 'import_vault' && (
                        <div className="space-y-4">
                            <div className="flex gap-2">
                                <GhostButton onClick={loadExcelPreview} disabled={loading}>
                                    <RefreshCw size={14} className="mr-1" /> Excel-Vorschau einlesen
                                </GhostButton>
                                <GhostButton onClick={loadVaultReconcile} disabled={loading}>
                                    <Shield size={14} className="mr-1" /> Vault- & Pilot-Abgleich
                                </GhostButton>
                            </div>

                            {/* Vault Matrix */}
                            {vaultMatrix && (
                                <div className="border border-white/10 bg-black/30 p-3 rounded-lg space-y-2 text-xs">
                                    <div className="font-semibold text-emerald-400">Pilotfall Verifikation (SL605LTE_EU003B / BEA605L2644)</div>
                                    <div className="text-stone-300">{vaultMatrix.summary}</div>
                                    <div className="space-y-2 pt-2">
                                        {vaultMatrix.matrix.map((m, idx) => (
                                            <div key={idx} className="bg-stone-900/60 p-2 rounded border border-white/5 font-mono text-[11px]">
                                                <div><strong>Quelle:</strong> {m.source} · Order: {m.orderLiteral} · SKU: {m.skuLiteral}</div>
                                                <div><strong>Bereich:</strong> {m.rangeStart} - {m.rangeEnd} ({m.quantity} Basiswerte, {m.formula})</div>
                                                <div><strong>Semantik:</strong> {m.boundarySemantics}</div>
                                                <div><strong>PIM-Bestand:</strong> Charge {m.pimHit?.batchNumber} (#{m.pimHit?.chargeId}, Status {m.pimHit?.status})</div>
                                                <div><strong>Entscheidung:</strong> <span className="text-emerald-400 font-bold">{m.decision}</span> ({m.status})</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Excel Preview */}
                            {excelData && (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-xs">
                                        <div className="flex gap-2 items-center">
                                            <span className="text-stone-400">Sheet:</span>
                                            {Object.keys(excelData.sheets).map(s => (
                                                <button
                                                    key={s}
                                                    onClick={() => setSelectedSheet(s)}
                                                    className={`px-2 py-1 rounded text-[11px] ${selectedSheet === s ? 'bg-blue-600 text-white' : 'bg-stone-800 text-stone-300'}`}
                                                >
                                                    {s} ({excelData.sheets[s].matchedRows}/{excelData.sheets[s].totalRows})
                                                </button>
                                            ))}
                                        </div>
                                        <span className="text-stone-500 font-mono text-[10px]">SHA256: {excelData.sha256.slice(0, 16)}...</span>
                                    </div>

                                    {selectedSheet && excelData.sheets[selectedSheet] && (
                                        <div className="bg-black/30 border border-white/5 rounded-lg overflow-x-auto max-h-[45vh]">
                                            <table className="w-full text-left border-collapse text-[11px] font-mono">
                                                <thead className="sticky top-0 bg-stone-900 border-b border-white/10 text-stone-400 font-sans">
                                                    <tr>
                                                        <th className="p-1.5">Z.</th>
                                                        <th className="p-1.5">Modell (Excel)</th>
                                                        <th className="p-1.5">Auftrag</th>
                                                        <th className="p-1.5 text-right">Menge</th>
                                                        <th className="p-1.5">Von</th>
                                                        <th className="p-1.5">Bis</th>
                                                        <th className="p-1.5">Formel</th>
                                                        <th className="p-1.5">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {excelData.sheets[selectedSheet].rows.map((r, i) => (
                                                        <tr key={i} className="border-b border-white/5 hover:bg-stone-800/40">
                                                            <td className="p-1.5 text-stone-500">{r.rowNumber}</td>
                                                            <td className="p-1.5 text-white font-sans">{r.modelRaw}</td>
                                                            <td className="p-1.5 text-stone-300">{r.order || '-'}</td>
                                                            <td className="p-1.5 text-right text-stone-300">{r.qty}</td>
                                                            <td className="p-1.5 text-stone-300">{r.from}</td>
                                                            <td className="p-1.5 text-stone-300">{r.to}</td>
                                                            <td className="p-1.5 text-stone-400 text-[10px]">{r.formula || '-'}</td>
                                                            <td className="p-1.5 font-sans">
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                                                    r.matchStatus === 'MATCH' ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700' :
                                                                    r.matchStatus === 'AMBIGUOUS_MAPPING' ? 'bg-amber-900/50 text-amber-300 border border-amber-700' :
                                                                    'bg-stone-800 text-stone-500'
                                                                }`}>
                                                                    {r.matchStatus}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Revoke Prompt Dialog */}
                {revokingAllocId && (
                    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
                        <div className="bg-stone-900 border border-red-500/50 rounded-lg p-4 max-w-md w-full space-y-3 text-xs">
                            <div className="font-bold text-white text-sm">Zuteilung aufheben</div>
                            <div className="text-stone-300">
                                Bitte geben Sie eine Begründung an. Diese Aktion wird revisionssicher im Audit-Log erfasst.
                            </div>
                            <textarea
                                value={revokeReason}
                                onChange={(e) => setRevokeReason(e.target.value)}
                                placeholder="Begründung (z.B. Fehlplanung, Auftragsstornierung)..."
                                className="w-full bg-black/50 border border-white/10 rounded p-2 text-white text-xs h-20"
                                required
                            />
                            <div className="flex justify-end gap-2">
                                <GhostButton onClick={() => setRevokingAllocId(null)}>Abbrechen</GhostButton>
                                <PrimaryButton onClick={handleRevoke} className="bg-red-600 hover:bg-red-500">
                                    Verbindlich aufheben
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="border-t border-white/10 pt-3 flex justify-between items-center mt-2 text-xs">
                    <span className="text-stone-500">
                        PIM IMEI-Management · Führendes Vergaberegister
                    </span>
                    <GhostButton onClick={onClose}>
                        Schließen
                    </GhostButton>
                </div>
            </div>
        </Modal>
    );
};
